/**
 * useStructuredFetch — one-shot fetch with manual refetch trigger.
 *
 * Generic over the response shape. Step 1 uses it for the
 * `getItemDefinition` call; future steps can use it for whatever
 * structured fetch they need (asset list, threat catalog, etc.).
 *
 * Lifecycle:
 *   - When `enabled && fetcher` are both truthy, fires the fetcher
 *     once. Stores result in `data`. Resets `data` to null when
 *     `enabled` flips to false.
 *   - `refetch()` bumps an internal version counter that re-runs
 *     the effect.
 *   - Auto-cancels the in-flight fetch on unmount (or on a new
 *     fetch starting) via AbortController.
 *
 * The hook is intentionally NOT a polling hook — that's `usePolling`.
 * This is for "fetch once after a status transition" patterns.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { StructuredFetchState } from '../step-components/stepBodyProps';

export interface UseStructuredFetchInput<TRaw, TData> {
  /** Async fetcher; receives an AbortSignal it should forward to the request. */
  fetcher: (signal: AbortSignal) => Promise<TRaw>;
  /** Pure transform from raw response → display data. */
  extract?: (raw: TRaw) => TData | null;
  /** When false, hook idles and clears `data`. */
  enabled: boolean;
}

export function useStructuredFetch<TRaw, TData = TRaw>({
  fetcher,
  extract,
  enabled,
}: UseStructuredFetchInput<TRaw, TData>): StructuredFetchState<TData> {
  const [data, setData] = useState<TData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState<number>(0);
  const abortRef = useRef<AbortController | null>(null);

  const refetch = useCallback(() => {
    setVersion((v) => v + 1);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setData(null);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    abortRef.current = new AbortController();
    setLoading(true);
    setError(null);
    fetcher(abortRef.current.signal)
      .then((raw) => {
        if (cancelled) return;
        const transformed = extract ? extract(raw) : (raw as unknown as TData);
        setData(transformed);
      })
      .catch((e: any) => {
        if (e?.name === 'AbortError' || e?.code === 'ERR_CANCELED') return;
        if (cancelled) return;
        setError(e?.response?.data?.detail || e?.message || 'Fetch failed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, [enabled, version, fetcher, extract]);

  return { data, loading, error, refetch };
}
